'use client';

import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';

export type ConsentType = 'voice' | 'face' | 'referenceAudio';

interface ConsentContent {
  linkText: string;
  href: string;
}

interface CreationConsentDialogProps {
  consentTypes: ConsentType[];
  dialogTip: string;
  agreePrefix: string;
  cancelText: string;
  confirmText: string;
  consentContentMap: Record<ConsentType, ConsentContent>;
  onCancel: () => void;
  onConfirm: (agreedConsentTypes: ConsentType[]) => void;
}

export function CreationConsentDialog({
  consentTypes,
  dialogTip,
  agreePrefix,
  cancelText,
  confirmText,
  consentContentMap,
  onCancel,
  onConfirm,
}: CreationConsentDialogProps) {
  const [agreedConsentMap, setAgreedConsentMap] = useState<Record<ConsentType, boolean>>({
    voice: false,
    face: false,
    referenceAudio: false,
  });
  const canConfirm = consentTypes.every((consentType) => agreedConsentMap[consentType]);

  return (
    <div className="space-y-4">
      <div className="rounded-16 bg-system-white04 p-4 text-sm text-system-white64 leading-6">{dialogTip}</div>

      <div className="space-y-3">
        {consentTypes.map((consentType) => (
          <div
            key={consentType}
            className="flex items-center gap-3 rounded-12 border border-system-white08 bg-system-white04 p-4"
          >
            <Checkbox
              className="shrink-0 text-base text-system-white"
              checked={agreedConsentMap[consentType]}
              onCheckedChange={(checked) =>
                setAgreedConsentMap((prev) => ({
                  ...prev,
                  [consentType]: Boolean(checked),
                }))
              }
            />
            <span className="text-sm text-system-white leading-5">
              {agreePrefix}
              <a
                className="text-system-white underline underline-offset-4"
                href={consentContentMap[consentType].href}
                rel="noreferrer"
                target="_blank"
              >
                {consentContentMap[consentType].linkText}
              </a>
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" size="dialog" onClick={onCancel}>
          {cancelText}
        </Button>
        <Button
          variant="primary"
          size="dialog"
          className="disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 disabled:opacity-100"
          disabled={!canConfirm}
          onClick={() => onConfirm(consentTypes)}
        >
          {confirmText}
        </Button>
      </div>
    </div>
  );
}
